import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, UserPlus, Mail, Trash2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { clientTeamService, type ClientTeamMember } from '@/services/clientTeamService';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface ClientTeamManagementProps {
  clientId: string;
  clientName: string;
  clientEmail?: string;
}

export const ClientTeamManagement: React.FC<ClientTeamManagementProps> = ({
  clientId,
  clientName,
  clientEmail
}) => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState<ClientTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'member'
  });

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const members = await clientTeamService.getClientTeamMembers(clientId);
      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeamMembers();
  }, [clientId]);

  // Real-time subscription for team members
  useEffect(() => {
    const channel = supabase
      .channel(`team-members-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_team_members',
          filter: `client_id=eq.${clientId}`
        },
        () => {
          loadTeamMembers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clientId]);

  // Validate email when it changes
  const handleEmailChange = (email: string) => {
    setInviteForm(prev => ({ ...prev, email }));
    
    // Check if email matches client email (case-insensitive)
    if (clientEmail && email.toLowerCase() === clientEmail.toLowerCase()) {
      setEmailError('Team members and clients must have different email addresses');
    } else {
      setEmailError(null);
    }
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Prevent adding client email as team member
    if (clientEmail && inviteForm.email.toLowerCase() === clientEmail.toLowerCase()) {
      toast.error('Team members and clients must have different email addresses');
      return;
    }

    // Check if email is already a team member
    const existingMember = teamMembers.find(m => m.email.toLowerCase() === inviteForm.email.toLowerCase());
    if (existingMember) {
      toast.error('This email is already a team member');
      return;
    }

    try {
      setInviteLoading(true);
      const invitation = await clientTeamService.inviteTeamMember({
        client_id: clientId,
        email: inviteForm.email,
        name: inviteForm.name,
        role: inviteForm.role
      });

      // Send invitation email
      const { error: emailError } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          email: inviteForm.email,
          name: inviteForm.name,
          role: inviteForm.role,
          clientName,
          inviterName: user?.user_metadata?.display_name || user?.email || 'Event Planner',
          invitationToken: invitation.invitation_token,
        },
      });

      if (emailError) {
        console.error("Error sending invitation email:", emailError);
        toast.error('Invitation created but email delivery failed. You can copy the link manually.');
      } else {
        toast.success('Team member invited! Email sent successfully.');
      }

      // Generate invitation link as backup
      const inviteUrl = `${window.location.origin}/client-invite/${invitation.invitation_token}`;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedToken(invitation.invitation_token);
      
      setInviteForm({ email: '', name: '', role: 'member' });
      setEmailError(null);
      setIsInviteDialogOpen(false);
      loadTeamMembers();
      
      // Clear copied state after 3 seconds
      setTimeout(() => setCopiedToken(null), 3000);
    } catch (error: any) {
      console.error('Error inviting team member:', error);
      if (error.message?.includes('duplicate')) {
        toast.error('This email is already invited or is a team member');
      } else {
        toast.error('Failed to invite team member');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this team member?')) return;

    try {
      await clientTeamService.removeTeamMember(memberId);
      toast.success('Team member removed');
      loadTeamMembers();
    } catch (error) {
      console.error('Error removing team member:', error);
      toast.error('Failed to remove team member');
    }
  };

  const toggleMemberStatus = async (memberId: string, currentStatus: boolean, memberEmail: string, memberName: string) => {
    try {
      await clientTeamService.updateTeamMemberStatus(memberId, !currentStatus);
      const newStatus = !currentStatus ? 'activated' : 'deactivated';
      toast.success(`Team member ${newStatus}`);
      
      // Send email notification
      await supabase.functions.invoke('send-team-status-notification', {
        body: {
          email: memberEmail,
          name: memberName,
          clientName,
          status: newStatus
        }
      });
      
      loadTeamMembers();
    } catch (error) {
      console.error('Error updating member status:', error);
      toast.error('Failed to update team member status');
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'manager': return 'default';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="ml-2 text-sm">Loading...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Team Members - {clientName}</CardTitle>
          <CardDescription className="text-sm">
            Manage team members who can access the client portal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end mb-4">
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="w-full sm:w-auto">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4 sm:mx-auto">
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                  <DialogDescription>
                    Send an invitation to join the client portal for {clientName}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInviteSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="member@company.com"
                      required
                      className={emailError ? 'border-destructive' : ''}
                    />
                    {emailError && (
                      <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {emailError}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select 
                      value={inviteForm.role} 
                      onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 pt-4">
                    <Button type="submit" disabled={inviteLoading || !!emailError} className="flex-1">
                      {inviteLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      Send Invitation
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsInviteDialogOpen(false);
                        setEmailError(null);
                        setInviteForm({ email: '', name: '', role: 'member' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {teamMembers.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <UserPlus className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm">No team members yet</p>
              <p className="text-xs">Invite team members to access the client portal</p>
            </div>
          ) : (
            <div className="space-y-3">
              {teamMembers.map((member) => (
                <div 
                  key={member.id} 
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{member.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant={getRoleBadgeVariant(member.role)} className="text-xs">
                        {member.role}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <div 
                          className={`h-2 w-2 rounded-full ${
                            member.is_active ? 'bg-green-500' : 'bg-red-500'
                          }`} 
                        />
                        <span className="text-xs text-muted-foreground">
                          {member.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {!member.user_id && (
                        <Badge variant="outline" className="text-xs">
                          Pending
                        </Badge>
                      )}
                    </div>
                    {member.joined_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Joined: {new Date(member.joined_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleMemberStatus(member.id, member.is_active, member.email, member.name)}
                    >
                      {member.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {copiedToken && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-green-800 dark:text-green-400">
              <Check className="h-4 w-4" />
              <span className="text-sm font-medium">
                Invitation link copied to clipboard! Share it with the team member.
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientTeamManagement;
